
import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Space, Tag, Modal, Form, Input,
    Select, Tooltip, Typography, message, Alert, Descriptions, Badge, Switch, Spin, Divider, Popconfirm
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, SyncOutlined,
    CheckCircleOutlined, ExclamationCircleOutlined, SettingOutlined,
    ArrowRightOutlined, BarChartOutlined, KeyOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import dnsService from '../../services/dnsService';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { confirm } = Modal;

// 随机单词列表（形容词 + 名词组合）
const adjectives = [
    'swift', 'bright', 'calm', 'eager', 'fair', 'gentle', 'happy', 'keen',
    'lively', 'noble', 'proud', 'quick', 'rapid', 'smart', 'vivid', 'warm',
    'cosmic', 'cyber', 'digital', 'electric', 'global', 'hyper', 'mega', 'neo',
    'prime', 'quantum', 'stellar', 'super', 'turbo', 'ultra', 'alpha', 'beta'
];
const nouns = [
    'cloud', 'storm', 'wave', 'star', 'moon', 'sun', 'wind', 'fire',
    'falcon', 'eagle', 'tiger', 'lion', 'wolf', 'bear', 'hawk', 'phoenix',
    'node', 'core', 'pulse', 'spark', 'flash', 'beam', 'bolt', 'force',
    'nexus', 'vertex', 'apex', 'prime', 'forge', 'vault', 'grid', 'mesh'
];

const generateRandomName = () => {
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    const num = Math.floor(Math.random() * 100);
    return `${adj}-${noun}-${num}`;
};

const generateRandomNsHostname = () => {
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    return `${adj}-${noun}.yeapcloud.net`;
};

const DnsNodes = () => {
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalTitle, setModalTitle] = useState('添加DNS节点');
    const [editingNode, setEditingNode] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [currentNode, setCurrentNode] = useState(null);
    const [syncZones, setSyncZones] = useState([]);
    const [form] = Form.useForm();
    const [nodeHealth, setNodeHealth] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [parentGroups, setParentGroups] = useState([]);
    const [childGroups, setChildGroups] = useState([]);
    const [groupLoading, setGroupLoading] = useState(false);
    const [selectedParentId, setSelectedParentId] = useState(null);
    const [parentForm] = Form.useForm();
    const [childForm] = Form.useForm();
    const [assignForm] = Form.useForm();
    const [parentModalVisible, setParentModalVisible] = useState(false);
    const [childModalVisible, setChildModalVisible] = useState(false);
    const [assignModalVisible, setAssignModalVisible] = useState(false);
    const [editingParent, setEditingParent] = useState(null);
    const [editingChild, setEditingChild] = useState(null);
    const [assigningChild, setAssigningChild] = useState(null);

    // Proxy config states
    const [proxyConfig, setProxyConfig] = useState(null);
    const [proxyUpstreams, setProxyUpstreams] = useState([]);
    const [proxyConfigLoading, setProxyConfigLoading] = useState(false);
    const [proxyConfigForm] = Form.useForm();
    const [upstreamModalVisible, setUpstreamModalVisible] = useState(false);
    const [editingUpstream, setEditingUpstream] = useState(null);
    const [upstreamForm] = Form.useForm();

    // 获取节点列表
    const fetchNodes = async () => {
        setLoading(true);
        try {
            const result = await dnsService.getDnsNodes();
            if (result.success) {
                setNodes(result.data);
            } else {
                message.error(result.error || '获取节点失败');
            }
        } catch (error) {
            message.error('获取节点失败: ' + (error.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNodes();
        fetchParentGroups();
        fetchChildGroups();
    }, []);

    // 显示添加节点模态框
    const showAddModal = () => {
        setModalTitle('添加DNS节点');
        setEditingNode(null);
        form.resetFields();
        form.setFieldsValue({
            generate_key: true,
            port: 53,
            node_type: 'dns_server',
            is_direct_connection: false,
            proxy_node_id: null,
            status: 'inactive'
        });
        setIsModalVisible(true);
    };

    const fetchParentGroups = async () => {
        setGroupLoading(true);
        try {
            const result = await dnsService.getNodeParentGroups();
            if (result.success) {
                setParentGroups(result.data || []);
            } else {
                message.error(result.error || '获取父节点组失败');
            }
        } catch (error) {
            message.error('获取父节点组失败: ' + (error.error || error.message));
        } finally {
            setGroupLoading(false);
        }
    };

    const fetchChildGroups = async (parentId = null) => {
        setGroupLoading(true);
        try {
            const result = await dnsService.getNodeChildGroups(parentId);
            if (result.success) {
                setChildGroups(result.data || []);
            } else {
                message.error(result.error || '获取子节点组失败');
            }
        } catch (error) {
            message.error('获取子节点组失败: ' + (error.error || error.message));
        } finally {
            setGroupLoading(false);
        }
    };

    // 显示编辑节点模态框
    const showEditModal = (node) => {
        setModalTitle(`编辑节点: ${node.hostname}`);
        setEditingNode(node);
        form.resetFields();
        form.setFieldsValue({
            hostname: node.hostname,
            ip_address: node.ip_address,
            port: node.port,
            node_type: node.node_type || 'dns_server',
            is_direct_connection: node.is_direct_connection || false,
            proxy_node_id: node.proxy_node_id || null,
            status: node.status,
            reset_key: false
        });
        setIsModalVisible(true);
    };

    const openParentModal = (parent = null) => {
        setEditingParent(parent);
        parentForm.resetFields();
        if (parent) {
            parentForm.setFieldsValue({
                id: parent.id,
                name: parent.name,
                description: parent.description,
                status: parent.status || 'active',
            });
        } else {
            parentForm.setFieldsValue({ status: 'active' });
        }
        setParentModalVisible(true);
    };

    const openChildModal = (child = null) => {
        setEditingChild(child);
        childForm.resetFields();
        if (child) {
            childForm.setFieldsValue({
                id: child.id,
                parent_group_id: child.parent_group_id,
                name: child.name,
                ns_hostname: child.ns_hostname,
                status: child.status || 'active',
            });
        } else {
            childForm.setFieldsValue({
                parent_group_id: selectedParentId || (parentGroups[0]?.id || null),
                status: 'active',
            });
        }
        setChildModalVisible(true);
    };

    const openAssignModal = (child) => {
        setAssigningChild(child);
        assignForm.resetFields();
        assignForm.setFieldsValue({
            node_ids: child.node_ids || []
        });
        setAssignModalVisible(true);
    };

    // 处理模态框提交
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingNode) {
                // 更新节点
                const result = await dnsService.updateDnsNode({
                    id: editingNode.id,
                    ...values
                });

                if (result.success) {
                    message.success('节点更新成功');
                    setIsModalVisible(false);

                    // 如果重置了密钥，显示新密钥
                    if (values.reset_key && result.data && result.data.sync_key) {
                        Modal.success({
                            title: '同步密钥已重置',
                            content: (
                                <div>
                                    <p>新的同步密钥已生成。请保存此密钥，它不会再次显示。</p>
                                    <Input.Password
                                        value={result.data.sync_key}
                                        readOnly
                                        addonAfter={
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(result.data.sync_key);
                                                    message.success('密钥已复制到剪贴板');
                                                }}
                                            >
                                                复制
                                            </Button>
                                        }
                                    />
                                </div>
                            ),
                        });
                    }

                    fetchNodes();
                } else {
                    message.error(result.error || '更新节点失败');
                }
            } else {
                // 创建新节点
                const result = await dnsService.createDnsNode(values);

                if (result.success) {
                    message.success('节点添加成功');
                    setIsModalVisible(false);

                    // 显示同步密钥
                    if (result.data && result.data.sync_key) {
                        Modal.success({
                            title: '节点添加成功',
                            content: (
                                <div>
                                    <p>同步密钥已生成。请保存此密钥，它不会再次显示。</p>
                                    <Input.Password
                                        value={result.data.sync_key}
                                        readOnly
                                        addonAfter={
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(result.data.sync_key);
                                                    message.success('密钥已复制到剪贴板');
                                                }}
                                            >
                                                复制
                                            </Button>
                                        }
                                    />
                                    <Alert
                                        style={{ marginTop: 16 }}
                                        message="节点配置说明"
                                        description={
                                            <ol>
                                                <li>将此密钥配置到节点的配置文件中</li>
                                                <li>确保节点的状态设置为"活跃"</li>
                                                <li>使用"同步"按钮将DNS配置推送到节点</li>
                                            </ol>
                                        }
                                        type="info"
                                        showIcon
                                    />
                                </div>
                            ),
                        });
                    }

                    fetchNodes();
                } else {
                    message.error(result.error || '添加节点失败');
                }
            }
        } catch (error) {
            console.log('Validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    // 删除节点
    const deleteNode = (node) => {
        confirm({
            title: '确认删除',
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除节点 "${node.hostname}" 吗？此操作无法撤销。`,
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                try {
                    setLoading(true);
                    const result = await dnsService.deleteDnsNode(node.id);

                    if (result.success) {
                        message.success('节点删除成功');
                        fetchNodes();
                    } else {
                        message.error(result.error || '删除节点失败');
                    }
                } catch (error) {
                    message.error('删除节点失败: ' + (error.error || error.message));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // 同步节点
    const syncNode = async (node) => {
        setLoading(true);
        try {
            const result = await dnsService.syncToNodes({
                node_id: node.id
            });

            if (result.success) {
                message.success('同步请求已发送');
                fetchNodes(); // 刷新节点状态
            } else {
                message.error(result.error || '同步节点失败');
            }
        } catch (error) {
            message.error('同步节点失败: ' + (error.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    // 查看节点详情
    const viewNodeDetails = async (node) => {
        setCurrentNode(node);
        setHealthLoading(true);
        setNodeHealth(null);
        setProxyConfig(null);
        setProxyUpstreams([]);

        try {
            // 获取节点同步状态
            const result = await dnsService.getNodeSyncStatus(node.id);

            if (result.success) {
                setSyncZones(result.data);
            } else {
                setSyncZones([]);
                message.error(result.error || '获取同步状态失败');
            }

            // 获取节点健康状态
            try {
                const healthResult = await dnsService.checkNodeHealth(node.id);
                if (healthResult.success) {
                    setNodeHealth(healthResult.data);
                }
            } catch (error) {
                console.error('Health check failed:', error);
            }

            // 如果是代理节点，加载代理配置
            if (node.node_type === 'dns_proxy') {
                await loadProxyConfig(node.id);
            }
        } catch (error) {
            message.error('获取节点详情失败: ' + (error.error || error.message));
        } finally {
            setHealthLoading(false);
            setDetailsVisible(true);
        }
    };

    // 加载代理配置
    const loadProxyConfig = async (nodeId) => {
        setProxyConfigLoading(true);
        try {
            const [configResult, upstreamsResult] = await Promise.all([
                dnsService.getProxyConfig(nodeId),
                dnsService.getProxyUpstreams(nodeId)
            ]);

            if (configResult.success) {
                setProxyConfig(configResult.data);
                proxyConfigForm.setFieldsValue(configResult.data);
            }

            if (upstreamsResult.success) {
                setProxyUpstreams(upstreamsResult.data || []);
            }
        } catch (error) {
            console.error('Failed to load proxy config:', error);
        } finally {
            setProxyConfigLoading(false);
        }
    };

    // 保存代理配置
    const saveProxyConfig = async () => {
        try {
            const values = await proxyConfigForm.validateFields();
            setProxyConfigLoading(true);

            const result = await dnsService.updateProxyConfig({
                node_id: currentNode.id,
                ...values
            });

            if (result.success) {
                message.success('代理配置已保存');
                setProxyConfig(result.data);
            } else {
                message.error(result.error || '保存配置失败');
            }
        } catch (error) {
            message.error('保存配置失败: ' + (error.error || error.message));
        } finally {
            setProxyConfigLoading(false);
        }
    };

    // 添加/编辑上游服务器
    const showUpstreamModal = (upstream = null) => {
        setEditingUpstream(upstream);
        upstreamForm.resetFields();
        if (upstream) {
            upstreamForm.setFieldsValue(upstream);
        } else {
            upstreamForm.setFieldsValue({ weight: 10, status: 'active' });
        }
        setUpstreamModalVisible(true);
    };

    const saveUpstream = async () => {
        try {
            const values = await upstreamForm.validateFields();
            setProxyConfigLoading(true);

            let result;
            if (editingUpstream) {
                result = await dnsService.updateProxyUpstream({
                    id: editingUpstream.id,
                    ...values
                });
            } else {
                result = await dnsService.addProxyUpstream({
                    node_id: currentNode.id,
                    ...values
                });
            }

            if (result.success) {
                message.success(editingUpstream ? '上游服务器已更新' : '上游服务器已添加');
                setUpstreamModalVisible(false);
                await loadProxyConfig(currentNode.id);
            } else {
                message.error(result.error || '操作失败');
            }
        } catch (error) {
            message.error('操作失败: ' + (error.error || error.message));
        } finally {
            setProxyConfigLoading(false);
        }
    };

    const deleteUpstream = async (upstream) => {
        confirm({
            title: '删除上游服务器',
            content: `确定要删除上游服务器 "${upstream.addr}" 吗？`,
            onOk: async () => {
                try {
                    setProxyConfigLoading(true);
                    const result = await dnsService.deleteProxyUpstream(upstream.id);
                    if (result.success) {
                        message.success('上游服务器已删除');
                        await loadProxyConfig(currentNode.id);
                    } else {
                        message.error(result.error || '删除失败');
                    }
                } catch (error) {
                    message.error('删除失败: ' + (error.error || error.message));
                } finally {
                    setProxyConfigLoading(false);
                }
            }
        });
    };

    // 同步特定区域到节点
    const syncZoneToNode = async (zoneId, nodeId) => {
        setLoading(true);
        try {
            const result = await dnsService.syncToNodes({
                zone_id: zoneId,
                node_id: nodeId
            });

            if (result.success) {
                message.success('区域同步请求已发送');

                // 刷新同步状态
                const statusResult = await dnsService.getNodeSyncStatus(nodeId);
                if (statusResult.success) {
                    setSyncZones(statusResult.data);
                }
            } else {
                message.error(result.error || '同步区域失败');
            }
        } catch (error) {
            message.error('同步区域失败: ' + (error.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    // 重置节点密钥
    const resetNodeKey = async (nodeId) => {
        confirm({
            title: '重置同步密钥',
            icon: <KeyOutlined />,
            content: '确定要重置此节点的同步密钥吗？这将需要在节点服务器上更新配置。',
            onOk: async () => {
                setLoading(true);
                try {
                    const result = await dnsService.resetNodeKey(nodeId);

                    if (result.success && result.data && result.data.sync_key) {
                        Modal.success({
                            title: '同步密钥已重置',
                            content: (
                                <div>
                                    <p>新的同步密钥已生成。请保存此密钥，它不会再次显示。</p>
                                    <Input.Password
                                        value={result.data.sync_key}
                                        readOnly
                                        addonAfter={
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(result.data.sync_key);
                                                    message.success('密钥已复制到剪贴板');
                                                }}
                                            >
                                                复制
                                            </Button>
                                        }
                                    />
                                </div>
                            ),
                        });
                        fetchNodes();
                    } else {
                        message.error(result.error || '重置密钥失败');
                    }
                } catch (error) {
                    message.error('重置密钥失败: ' + (error.error || error.message));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleSaveParent = async () => {
        try {
            const values = await parentForm.validateFields();
            const result = await dnsService.saveNodeParentGroup(values);
            if (result.success) {
                message.success('父节点组已保存');
                setParentModalVisible(false);
                fetchParentGroups();
            } else {
                message.error(result.error || '保存失败');
            }
        } catch (error) {
            message.error('保存失败：' + (error.error || error.message || '未知错误'));
        }
    };

    const handleSaveChild = async () => {
        try {
            const values = await childForm.validateFields();
            const result = await dnsService.saveNodeChildGroup(values);
            if (result.success) {
                message.success('子节点组已保存');
                setChildModalVisible(false);
                setSelectedParentId(values.parent_group_id);
                fetchChildGroups(values.parent_group_id);
                fetchParentGroups();
            } else {
                message.error(result.error || '保存失败');
            }
        } catch (error) {
            message.error('保存失败：' + (error.error || error.message || '未知错误'));
        }
    };

    const handleAssignNodes = async () => {
        if (!assigningChild) return;
        try {
            const values = await assignForm.validateFields();
            const result = await dnsService.assignChildGroupNodes(assigningChild.id, values.node_ids || []);
            if (result.success) {
                message.success('节点绑定已更新');
                setAssignModalVisible(false);
                fetchChildGroups(selectedParentId);
            } else {
                message.error(result.error || '更新失败');
            }
        } catch (error) {
            message.error('更新失败：' + (error.error || error.message || '未知错误'));
        }
    };

    const handleDeleteParent = async (parent) => {
        try {
            const result = await dnsService.deleteNodeParentGroup(parent.id);
            if (result.success) {
                message.success('NS 池已删除');
                fetchParentGroups();
                fetchChildGroups(selectedParentId);
            } else {
                message.error(result.error || '删除失败');
            }
        } catch (error) {
            message.error('删除失败：' + (error.error || error.message || '未知错误'));
        }
    };

    const handleDeleteChild = async (child) => {
        try {
            const result = await dnsService.deleteNodeChildGroup(child.id);
            if (result.success) {
                message.success('NS 已删除');
                fetchChildGroups(selectedParentId);
                fetchParentGroups();
            } else {
                message.error(result.error || '删除失败');
            }
        } catch (error) {
            message.error('删除失败：' + (error.error || error.message || '未知错误'));
        }
    };

    // 检查节点健康状态
    const checkNodeHealth = async (nodeId) => {
        setHealthLoading(true);
        try {
            const result = await dnsService.checkNodeHealth(nodeId);

            if (result.success) {
                setNodeHealth(result.data);
                message.success('节点健康检查完成');
            } else {
                message.error(result.error || '节点健康检查失败');
            }
        } catch (error) {
            message.error('节点健康检查失败: ' + (error.error || error.message));
        } finally {
            setHealthLoading(false);
        }
    };

    // 根据 health_status 渲染健康状态标签（后端：healthy/warning/offline/unknown）
    const renderHealthBadge = (healthStatus) => {
        const map = {
            healthy: { color: 'success', text: '健康' },
            warning: { color: 'warning', text: '警告' },
            offline: { color: 'error', text: '离线' },
            unknown: { color: 'default', text: '未知' },
        };
        const cfg = map[healthStatus] || map.unknown;
        return <Badge status={cfg.color} text={cfg.text} />;
    };

    const columns = [
        {
            title: '主机名',
            dataIndex: 'hostname',
            key: 'hostname',
            render: text => <a>{text}</a>,
        },
        {
            title: 'IP地址',
            dataIndex: 'ip_address',
            key: 'ip_address',
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: status => {
                let color = 'default';
                let text = '未知';

                if (status === 'active') {
                    color = 'success';
                    text = '活跃';
                } else if (status === 'inactive') {
                    color = 'default';
                    text = '未激活';
                } else if (status === 'maintenance') {
                    color = 'warning';
                    text = '维护中';
                }

                return <Tag color={color}>{text}</Tag>;
            }
        },
        {
            title: '健康',
            dataIndex: 'health_status',
            key: 'health_status',
            render: healthStatus => renderHealthBadge(healthStatus)
        },
        {
            title: '上次上报',
            dataIndex: 'last_seen',
            key: 'last_seen',
            render: lastSeen => lastSeen ? new Date(lastSeen).toLocaleString() : '从未'
        },
        {
            title: '端口',
            dataIndex: 'port',
            key: 'port',
            render: port => port || 53
        },
        {
            title: '节点类型',
            dataIndex: 'node_type',
            key: 'node_type',
            render: nodeType => {
                if (nodeType === 'dns_proxy') {
                    return <Tag color="blue">DNS 代理</Tag>;
                }
                return <Tag color="green">DNS 服务器</Tag>;
            }
        },
        {
            title: '连接方式',
            key: 'connection',
            render: (_, record) => {
                if (record.node_type === 'dns_proxy') {
                    // 代理节点本身
                    const proxiedCount = nodes.filter(n => n.proxy_node_id === record.id).length;
                    return <Tag color="purple">代理 ({proxiedCount} 节点)</Tag>;
                }
                if (record.is_direct_connection) {
                    return <Tag color="green">直连</Tag>;
                }
                if (record.proxy_node_id) {
                    const proxyNode = nodes.find(n => n.id === record.proxy_node_id);
                    return (
                        <Tooltip title={proxyNode ? `代理: ${proxyNode.hostname}` : '代理节点'}>
                            <Tag color="orange">经代理</Tag>
                        </Tooltip>
                    );
                }
                return <Tag color="default">未配置</Tag>;
            }
        },
        {
            title: '上次同步',
            dataIndex: 'last_sync',
            key: 'last_sync',
            render: (lastSync) => lastSync ? new Date(lastSync).toLocaleString() : '从未'
        },
        {
            title: '区域数',
            dataIndex: 'zone_count',
            key: 'zone_count',
            render: count => count || 0
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="查看详情">
                        <Button
                            type="primary"
                            icon={<BarChartOutlined />}
                            size="small"
                            onClick={() => viewNodeDetails(record)}
                        />
                    </Tooltip>
                    <Tooltip title="编辑">
                        <Button
                            type="default"
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => showEditModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="同步">
                        <Button
                            type="default"
                            icon={<SyncOutlined />}
                            size="small"
                            onClick={() => syncNode(record)}
                        />
                    </Tooltip>
                    <Tooltip title="删除">
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            onClick={() => deleteNode(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Title level={2}>DNS节点管理</Title>

            <Card
                title="DNS节点列表"
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={showAddModal}
                    >
                        添加节点
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    dataSource={nodes}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                />
            </Card>

            <Divider />

            <Space style={{ width: '100%' }} direction="vertical" size="large">
                <Card
                    title="DNS 父节点组"
                    extra={
                        <Button type="primary" onClick={() => openParentModal()}>
                            新建父节点组
                        </Button>
                    }
                    loading={groupLoading}
                >
                    <Table
                        columns={[
                            { title: 'ID', dataIndex: 'id', width: 80 },
                            { title: '名称', dataIndex: 'name' },
                            { title: '状态', dataIndex: 'status', render: (s) => <Tag color={s === 'active' ? 'green' : 'default'}>{s || '-'}</Tag> },
                            {
                                title: '操作',
                                width: 200,
                                render: (_, record) => (
                                    <Space size="small">
                                        <Button size="small" icon={<EditOutlined />} onClick={() => openParentModal(record)}>
                                            编辑
                                        </Button>
                                        <Popconfirm
                                            title="确定删除该 NS 池？"
                                            okText="删除"
                                            okType="danger"
                                            cancelText="取消"
                                            onConfirm={() => handleDeleteParent(record)}
                                        >
                                            <Button size="small" danger icon={<DeleteOutlined />}>
                                                删除
                                            </Button>
                                        </Popconfirm>
                                    </Space>
                                ),
                            },
                        ]}
                        dataSource={parentGroups}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                </Card>

                <Card
                    title="DNS 子节点组"
                    extra={
                        <Space>
                            <Select
                                style={{ width: 200 }}
                                placeholder="按父组筛选"
                                allowClear
                                value={selectedParentId || undefined}
                                onChange={(val) => {
                                    setSelectedParentId(val || null);
                                    fetchChildGroups(val || null);
                                }}
                            >
                                {parentGroups.map((p) => (
                                    <Option key={p.id} value={p.id}>
                                        {p.name} (ID:{p.id})
                                    </Option>
                                ))}
                            </Select>
                            <Button type="primary" onClick={() => openChildModal()}>
                                新建子节点组
                            </Button>
                        </Space>
                    }
                    loading={groupLoading}
                >
                    <Table
                        columns={[
                            { title: 'ID', dataIndex: 'id', width: 70 },
                            { title: '名称', dataIndex: 'name' },
                            { title: 'NS 主机名', dataIndex: 'ns_hostname' },
                            { title: '父组ID', dataIndex: 'parent_group_id', width: 90 },
                            {
                                title: '状态',
                                dataIndex: 'status',
                                render: (s) => <Tag color={s === 'active' ? 'green' : 'default'}>{s || '-'}</Tag>,
                                width: 90,
                            },
                            {
                                title: '绑定节点数',
                                dataIndex: 'node_ids',
                                render: (ids) => (ids ? ids.length : 0),
                                width: 100,
                            },
                            {
                                title: '操作',
                                width: 280,
                                render: (_, record) => (
                                    <Space size="small">
                                        <Button size="small" icon={<EditOutlined />} onClick={() => openChildModal(record)}>
                                            编辑
                                        </Button>
                                        <Button size="small" icon={<SettingOutlined />} onClick={() => openAssignModal(record)}>
                                            绑定节点
                                        </Button>
                                        <Popconfirm
                                            title="确定删除该 NS？"
                                            okText="删除"
                                            okType="danger"
                                            cancelText="取消"
                                            onConfirm={() => handleDeleteChild(record)}
                                        >
                                            <Button size="small" danger icon={<DeleteOutlined />}>
                                                删除
                                            </Button>
                                        </Popconfirm>
                                    </Space>
                                ),
                            },
                        ]}
                        dataSource={selectedParentId ? childGroups.filter((c) => c.parent_group_id === selectedParentId) : childGroups}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                </Card>
            </Space>

            {/* 添加/编辑节点模态框 */}
            <Modal
                title={modalTitle}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                confirmLoading={loading}
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="hostname"
                        label="主机名"
                        rules={[{ required: true, message: '请输入主机名!' }]}
                    >
                        <Input placeholder="例如: dns1.example.com" />
                    </Form.Item>

                    <Form.Item
                        name="ip_address"
                        label="IP地址"
                        rules={[
                            { required: true, message: '请输入IP地址!' },
                            {
                                pattern: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$|^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::$|^::1$/,
                                message: '请输入有效的IPv4或IPv6地址!'
                            }
                        ]}
                    >
                        <Input placeholder="例如: 192.168.1.10" />
                    </Form.Item>

                    <Form.Item
                        name="port"
                        label="DNS端口"
                        rules={[{ required: true, message: '请输入端口号!' }]}
                    >
                        <Input type="number" placeholder="默认: 53" />
                    </Form.Item>

                    <Form.Item
                        name="node_type"
                        label="节点类型"
                        rules={[{ required: true, message: '请选择节点类型!' }]}
                    >
                        <Select>
                            <Option value="dns_server">DNS 服务器</Option>
                            <Option value="dns_proxy">DNS 代理</Option>
                        </Select>
                    </Form.Item>

                    {/* 只对 dns_server 类型显示直连设置 */}
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.node_type !== currentValues.node_type}
                    >
                        {({ getFieldValue }) => {
                            return getFieldValue('node_type') === 'dns_server' ? (
                                <>
                                    <Form.Item
                                        name="is_direct_connection"
                                        label="直连节点"
                                        valuePropName="checked"
                                        help="启用后，终端用户直接连接此节点；禁用则需通过代理节点"
                                    >
                                        <Switch />
                                    </Form.Item>

                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(prevValues, currentValues) => prevValues.is_direct_connection !== currentValues.is_direct_connection}
                                    >
                                        {({ getFieldValue: getField }) => {
                                            return !getField('is_direct_connection') ? (
                                                <Form.Item
                                                    name="proxy_node_id"
                                                    label="代理节点"
                                                    help="选择处理此节点流量的代理节点"
                                                >
                                                    <Select allowClear placeholder="选择代理节点">
                                                        {nodes
                                                            .filter(n => n.node_type === 'dns_proxy' && n.id !== editingNode?.id)
                                                            .map(n => (
                                                                <Option key={n.id} value={n.id}>
                                                                    {n.hostname} ({n.ip_address})
                                                                </Option>
                                                            ))
                                                        }
                                                    </Select>
                                                </Form.Item>
                                            ) : null;
                                        }}
                                    </Form.Item>
                                </>
                            ) : null;
                        }}
                    </Form.Item>

                    {editingNode ? (
                        <Form.Item
                            name="status"
                            label="状态"
                        >
                            <Select>
                                <Option value="active">活跃</Option>
                                <Option value="inactive">未激活</Option>
                                <Option value="maintenance">维护中</Option>
                            </Select>
                        </Form.Item>
                    ) : null}

                    {editingNode ? (
                        <Form.Item
                            name="reset_key"
                            valuePropName="checked"
                            label="重置同步密钥"
                        >
                            <Switch />
                        </Form.Item>
                    ) : (
                        <Form.Item
                            name="generate_key"
                            valuePropName="checked"
                            label="自动生成同步密钥"
                            help="如不勾选，则需要手动提供同步密钥"
                        >
                            <Switch defaultChecked />
                        </Form.Item>
                    )}

                    {editingNode ? null : (
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.generate_key !== currentValues.generate_key}
                        >
                            {({ getFieldValue }) => {
                                return !getFieldValue('generate_key') ? (
                                    <Form.Item
                                        name="sync_key"
                                        label="同步密钥"
                                        rules={[{ required: true, message: '请输入同步密钥!' }]}
                                    >
                                        <Input.Password placeholder="输入安全的同步密钥" />
                                    </Form.Item>
                                ) : null;
                            }}
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            {/* 父节点组模态框 */}
            <Modal
                title={editingParent ? '编辑父节点组' : '新建父节点组'}
                open={parentModalVisible}
                onOk={handleSaveParent}
                onCancel={() => setParentModalVisible(false)}
                destroyOnClose
                confirmLoading={groupLoading}
            >
                <Form layout="vertical" form={parentForm}>
                    <Form.Item name="id" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label="名称"
                        rules={[{ required: true, message: '请输入名称' }]}
                    >
                        <Input placeholder="例如：公共NS组" />
                    </Form.Item>
                    <Form.Item name="description" label="描述">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="status" label="状态" initialValue="active">
                        <Select>
                            <Option value="active">active</Option>
                            <Option value="inactive">inactive</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 子节点组模态框 */}
            <Modal
                title={editingChild ? '编辑子节点组' : '新建子节点组'}
                open={childModalVisible}
                onOk={handleSaveChild}
                onCancel={() => setChildModalVisible(false)}
                destroyOnClose
                confirmLoading={groupLoading}
            >
                <Form layout="vertical" form={childForm}>
                    <Form.Item name="id" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="parent_group_id"
                        label="父节点组"
                        rules={[{ required: true, message: '请选择父节点组' }]}
                    >
                        <Select placeholder="选择父节点组">
                            {parentGroups.map((p) => (
                                <Option key={p.id} value={p.id}>
                                    {p.name} (ID:{p.id})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label="名称"
                        rules={[{ required: true, message: '请输入名称' }]}
                    >
                        <Input
                            placeholder="例如：华东-NS组"
                            addonAfter={
                                <Tooltip title="生成随机名称">
                                    <ThunderboltOutlined
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => childForm.setFieldsValue({ name: generateRandomName() })}
                                    />
                                </Tooltip>
                            }
                        />
                    </Form.Item>
                    <Form.Item
                        name="ns_hostname"
                        label="NS主机名"
                        rules={[{ required: true, message: '请输入NS主机名' }]}
                    >
                        <Input
                            placeholder="例如：ns1.example.com"
                            addonAfter={
                                <Tooltip title="生成随机NS主机名">
                                    <ThunderboltOutlined
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => childForm.setFieldsValue({ ns_hostname: generateRandomNsHostname() })}
                                    />
                                </Tooltip>
                            }
                        />
                    </Form.Item>
                    <Form.Item name="status" label="状态" initialValue="active">
                        <Select>
                            <Option value="active">active</Option>
                            <Option value="inactive">inactive</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 子节点组绑定节点 */}
            <Modal
                title={assigningChild ? `绑定节点：${assigningChild.name}` : '绑定节点'}
                open={assignModalVisible}
                onOk={handleAssignNodes}
                onCancel={() => setAssignModalVisible(false)}
                destroyOnClose
                confirmLoading={groupLoading}
            >
                <Form layout="vertical" form={assignForm}>
                    <Form.Item name="node_ids" label="选择节点" rules={[{ required: true, message: '请选择节点' }]}>
                        <Select mode="multiple" placeholder="选择要绑定的节点">
                            {nodes.map((n) => (
                                <Option key={n.id} value={n.id}>
                                    {n.hostname} ({n.ip_address})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 节点详情模态框 */}
            <Modal
                title={currentNode ? `节点详情: ${currentNode.hostname}` : '节点详情'}
                open={detailsVisible}
                onCancel={() => setDetailsVisible(false)}
                width={800}
                footer={[
                    <Button key="close" onClick={() => setDetailsVisible(false)}>
                        关闭
                    </Button>
                ]}
            >
                {currentNode && (
                    <>
                        <Descriptions
                            title="基本信息"
                            bordered
                            column={2}
                            extra={
                                <Space>
                                    <Button
                                        icon={<SyncOutlined />}
                                        onClick={() => syncNode(currentNode)}
                                        loading={loading}
                                    >
                                        同步所有区域
                                    </Button>
                                    <Button
                                        icon={<KeyOutlined />}
                                        onClick={() => resetNodeKey(currentNode.id)}
                                        loading={loading}
                                    >
                                        重置密钥
                                    </Button>
                                </Space>
                            }
                        >
                            <Descriptions.Item label="主机名">{currentNode.hostname}</Descriptions.Item>
                            <Descriptions.Item label="IP地址">{currentNode.ip_address}</Descriptions.Item>
                            <Descriptions.Item label="端口">{currentNode.port || 53}</Descriptions.Item>
                            <Descriptions.Item label="节点类型">
                                <Tag color={currentNode.node_type === 'dns_proxy' ? 'blue' : 'green'}>
                                    {currentNode.node_type === 'dns_proxy' ? 'DNS 代理' : 'DNS 服务器'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="状态">
                                <Badge
                                    status={
                                        currentNode.status === 'active' ? 'success' :
                                            currentNode.status === 'maintenance' ? 'warning' : 'default'
                                    }
                                    text={
                                        currentNode.status === 'active' ? '活跃' :
                                            currentNode.status === 'maintenance' ? '维护中' : '未激活'
                                    }
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="上次同步">
                                {currentNode.last_sync ? new Date(currentNode.last_sync).toLocaleString() : '从未'}
                            </Descriptions.Item>
                            <Descriptions.Item label="创建时间">
                                {new Date(currentNode.created_at).toLocaleString()}
                            </Descriptions.Item>
                        </Descriptions>

                        <div style={{ marginTop: 16 }}>
                            <Card
                                type="inner"
                                title="健康检查"
                                extra={
                                    <Button
                                        type="link"
                                        onClick={() => checkNodeHealth(currentNode.id)}
                                        loading={healthLoading}
                                    >
                                        重新检查
                                    </Button>
                                }
                            >
                                {healthLoading ? (
                                    <div style={{ textAlign: 'center', padding: 24 }}>
                                        <Spin />
                                        <div style={{ marginTop: 8 }}>检查节点健康状态...</div>
                                    </div>
                                ) : nodeHealth ? (
                                    <Descriptions column={2} bordered>
                                        <Descriptions.Item label="健康状态">
                                            {renderHealthBadge(nodeHealth.health_status)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="节点状态">
                                            {nodeHealth.status || '未知'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="上次上报">
                                            {nodeHealth.last_seen ? new Date(nodeHealth.last_seen).toLocaleString() : '从未'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="距上次上报">
                                            {nodeHealth.last_seen && nodeHealth.minutes_since_seen != null
                                                ? `${nodeHealth.minutes_since_seen} 分钟前`
                                                : '未知'}
                                        </Descriptions.Item>
                                    </Descriptions>
                                ) : (
                                    <Alert
                                        message="未进行健康检查"
                                        description="点击重新检查按钮获取节点健康状态"
                                        type="info"
                                        showIcon
                                    />
                                )}
                            </Card>
                        </div>

                        <div style={{ marginTop: 16 }}>
                            <Card
                                type="inner"
                                title="节点同步状态"
                                extra={
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<SyncOutlined />}
                                        onClick={() => syncNode(currentNode)}
                                        loading={loading}
                                    >
                                        同步
                                    </Button>
                                }
                            >
                                {/* 后端 /dns/nodes/sync-status 返回的是单个节点状态摘要，而非区域数组 */}
                                {syncZones && !Array.isArray(syncZones) ? (
                                    <Descriptions column={2} bordered>
                                        <Descriptions.Item label="健康状态">
                                            {renderHealthBadge(syncZones.health_status)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="节点状态">
                                            {syncZones.status || '未知'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="上次同步">
                                            {syncZones.last_sync ? new Date(syncZones.last_sync).toLocaleString() : '从未'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="上次上报">
                                            {syncZones.last_seen ? new Date(syncZones.last_seen).toLocaleString() : '从未'}
                                        </Descriptions.Item>
                                    </Descriptions>
                                ) : (
                                    <Alert
                                        message="暂无同步状态"
                                        description="尚未获取到该节点的同步状态信息。"
                                        type="info"
                                        showIcon
                                    />
                                )}
                            </Card>
                        </div>

                        {/* 代理配置部分 - 仅对 dns_proxy 节点显示 */}
                        {currentNode.node_type === 'dns_proxy' && (
                            <div style={{ marginTop: 16 }}>
                                <Card
                                    type="inner"
                                    title="代理配置"
                                    extra={
                                        <Button
                                            type="primary"
                                            onClick={saveProxyConfig}
                                            loading={proxyConfigLoading}
                                        >
                                            保存配置
                                        </Button>
                                    }
                                >
                                    {proxyConfigLoading && !proxyConfig ? (
                                        <div style={{ textAlign: 'center', padding: 24 }}>
                                            <Spin />
                                            <div style={{ marginTop: 8 }}>加载代理配置...</div>
                                        </div>
                                    ) : (
                                        <Form
                                            form={proxyConfigForm}
                                            layout="vertical"
                                        >
                                            <Divider orientation="left">速率限制</Divider>
                                            <Space size="large" wrap>
                                                <Form.Item
                                                    name="rate_limit_enabled"
                                                    label="启用速率限制"
                                                    valuePropName="checked"
                                                >
                                                    <Switch />
                                                </Form.Item>
                                                <Form.Item
                                                    name="rate_limit_rps"
                                                    label="每秒请求数 (RPS)"
                                                >
                                                    <Input type="number" style={{ width: 120 }} />
                                                </Form.Item>
                                                <Form.Item
                                                    name="rate_limit_burst"
                                                    label="突发限制"
                                                >
                                                    <Input type="number" style={{ width: 120 }} />
                                                </Form.Item>
                                            </Space>

                                            <Divider orientation="left">查询设置</Divider>
                                            <Space size="large" wrap>
                                                <Form.Item
                                                    name="max_concurrent"
                                                    label="最大并发查询"
                                                >
                                                    <Input type="number" style={{ width: 120 }} />
                                                </Form.Item>
                                                <Form.Item
                                                    name="query_timeout_ms"
                                                    label="查询超时 (ms)"
                                                >
                                                    <Input type="number" style={{ width: 120 }} />
                                                </Form.Item>
                                            </Space>

                                            <Divider orientation="left">健康检查</Divider>
                                            <Space size="large" wrap>
                                                <Form.Item
                                                    name="health_check_interval_sec"
                                                    label="检查间隔 (秒)"
                                                >
                                                    <Input type="number" style={{ width: 120 }} />
                                                </Form.Item>
                                                <Form.Item
                                                    name="health_check_timeout_ms"
                                                    label="检查超时 (ms)"
                                                >
                                                    <Input type="number" style={{ width: 120 }} />
                                                </Form.Item>
                                            </Space>

                                            <Divider orientation="left">同步间隔</Divider>
                                            <Space size="large" wrap>
                                                <Form.Item
                                                    name="blacklist_sync_interval_sec"
                                                    label="黑名单同步 (秒)"
                                                >
                                                    <Input type="number" style={{ width: 120 }} />
                                                </Form.Item>
                                                <Form.Item
                                                    name="qos_sync_interval_sec"
                                                    label="QoS同步 (秒)"
                                                >
                                                    <Input type="number" style={{ width: 120 }} />
                                                </Form.Item>
                                            </Space>

                                            {proxyConfig && (
                                                <div style={{ marginTop: 16, color: '#888' }}>
                                                    配置版本: {proxyConfig.config_version}
                                                </div>
                                            )}
                                        </Form>
                                    )}
                                </Card>

                                <Card
                                    type="inner"
                                    title="上游DNS服务器"
                                    style={{ marginTop: 16 }}
                                    extra={
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => showUpstreamModal()}
                                        >
                                            添加上游
                                        </Button>
                                    }
                                >
                                    <Table
                                        dataSource={proxyUpstreams}
                                        rowKey="id"
                                        pagination={false}
                                        loading={proxyConfigLoading}
                                        columns={[
                                            {
                                                title: '地址',
                                                dataIndex: 'addr',
                                                key: 'addr',
                                            },
                                            {
                                                title: '权重',
                                                dataIndex: 'weight',
                                                key: 'weight',
                                            },
                                            {
                                                title: '状态',
                                                dataIndex: 'status',
                                                key: 'status',
                                                render: status => (
                                                    <Tag color={status === 'active' ? 'success' : 'default'}>
                                                        {status === 'active' ? '活跃' : '禁用'}
                                                    </Tag>
                                                )
                                            },
                                            {
                                                title: '操作',
                                                key: 'action',
                                                render: (_, record) => (
                                                    <Space>
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            icon={<EditOutlined />}
                                                            onClick={() => showUpstreamModal(record)}
                                                        >
                                                            编辑
                                                        </Button>
                                                        <Button
                                                            type="link"
                                                            danger
                                                            size="small"
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => deleteUpstream(record)}
                                                        >
                                                            删除
                                                        </Button>
                                                    </Space>
                                                )
                                            }
                                        ]}
                                    />
                                </Card>
                            </div>
                        )}
                    </>
                )}
            </Modal>

            {/* 上游服务器模态框 */}
            <Modal
                title={editingUpstream ? '编辑上游服务器' : '添加上游服务器'}
                open={upstreamModalVisible}
                onOk={saveUpstream}
                onCancel={() => setUpstreamModalVisible(false)}
                confirmLoading={proxyConfigLoading}
            >
                <Form
                    form={upstreamForm}
                    layout="vertical"
                >
                    <Form.Item
                        name="addr"
                        label="服务器地址"
                        rules={[
                            { required: true, message: '请输入服务器地址!' },
                            { pattern: /^[\d\.]+:\d+$|^\[[\da-fA-F:]+\]:\d+$/, message: '格式: IP:端口' }
                        ]}
                    >
                        <Input placeholder="例如: 8.8.8.8:53" />
                    </Form.Item>
                    <Form.Item
                        name="weight"
                        label="权重"
                        rules={[{ required: true, message: '请输入权重!' }]}
                    >
                        <Input type="number" placeholder="默认: 10" />
                    </Form.Item>
                    <Form.Item
                        name="status"
                        label="状态"
                    >
                        <Select>
                            <Option value="active">活跃</Option>
                            <Option value="inactive">禁用</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DnsNodes;
